import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronRight, ChevronDown, Search } from "lucide-react";

/**
 * CategoryTreePicker
 * Full-screen picker with sticky search bar at top (keyboard-safe).
 * Props: categories (flat list w/ parent_id), value, onChange(id, name), placeholder
 */
export default function CategoryTreePicker({ categories = [], value, onChange, placeholder = "Select a category" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const byParent = useMemo(() => {
    const m = new Map();
    for (const c of categories) {
      const k = c.parent_id || "__root__";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return m;
  }, [categories]);

  // Breadcrumb path per category
  const pathById = useMemo(() => {
    const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
    const cache = {};
    const build = (id) => {
      if (cache[id] !== undefined) return cache[id];
      const c = byId[id];
      if (!c) return (cache[id] = "");
      if (!c.parent_id) return (cache[id] = c.name);
      return (cache[id] = build(c.parent_id) + " › " + c.name);
    };
    categories.forEach((c) => build(c.id));
    return cache;
  }, [categories]);

  // Flatten the tree for the browse view (DFS with depth info)
  const flat = useMemo(() => {
    const out = [];
    const walk = (parent, depth, ancestors) => {
      const kids = byParent.get(parent) || [];
      for (const c of kids) {
        const children = byParent.get(c.id) || [];
        out.push({
          cat: c,
          depth,
          hasChildren: children.length > 0,
          ancestors,
        });
        walk(c.id, depth + 1, [...ancestors, c.id]);
      }
    };
    walk("__root__", 0, []);
    return out;
  }, [byParent]);

  // Only rows whose ancestors are all expanded (or roots) are visible
  const visibleFlat = useMemo(
    () => flat.filter((row) => row.ancestors.every((aid) => expanded.has(aid))),
    [flat, expanded],
  );

  const selected = categories.find((c) => c.id === value);

  const filtered = useMemo(() => {
    if (!q.trim()) return null;
    const needle = q.toLowerCase();
    return categories
      .filter((c) => {
        const path = (pathById[c.id] || c.name).toLowerCase();
        return c.name.toLowerCase().includes(needle) || path.includes(needle);
      })
      .slice(0, 60);
  }, [q, categories, pathById]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const pick = (c) => {
    onChange(c.id, c.name);
    setOpen(false);
    setQ("");
  };

  const close = () => {
    setOpen(false);
    setQ("");
  };

  return (
    <>
      <button
        type="button"
        data-testid="cat-picker-open"
        onClick={() => setOpen(true)}
        className="w-full h-11 rounded-xl px-3 text-sm text-left flex items-center justify-between"
        style={{
          border: "1px solid rgba(27,25,23,0.15)",
          background: "var(--paper)",
          color: selected ? "var(--ink)" : "var(--ink-3)",
        }}
      >
        <span className="truncate">{selected ? (pathById[selected.id] || selected.name) : placeholder}</span>
        <ChevronRight size={14} className="opacity-50" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/40"
            onClick={close}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ type: "spring", damping: 32, stiffness: 320 }}
              className="absolute inset-0 flex flex-col"
              style={{ background: "var(--paper)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sticky header: title + search bar. Always visible above keyboard. */}
              <div
                className="shrink-0 px-4 pt-4 pb-3"
                style={{
                  background: "var(--paper)",
                  borderBottom: "1px solid rgba(27,25,23,0.06)",
                  paddingTop: "max(1rem, env(safe-area-inset-top))",
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-editorial uppercase" style={{ color: "var(--ink-3)" }}>
                    Pick a category
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    className="w-10 h-10 -mr-2 rounded-full grid place-items-center press"
                    data-testid="cat-picker-close"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div
                  className="flex items-center gap-2 rounded-full h-11 px-3"
                  style={{ border: "1px solid rgba(27,25,23,0.15)", background: "var(--bg)" }}
                >
                  <Search size={16} strokeWidth={1.6} style={{ color: "var(--ink-3)" }} />
                  <input
                    autoFocus
                    inputMode="search"
                    enterKeyHint="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search category…"
                    data-testid="cat-picker-search"
                    className="flex-1 outline-none text-sm bg-transparent"
                  />
                  {q && (
                    <button
                      type="button"
                      onClick={() => setQ("")}
                      className="w-6 h-6 grid place-items-center press"
                      aria-label="Clear"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              {/* Scrollable results / tree. Padding-bottom for keyboard breathing room. */}
              <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-32">
                {filtered ? (
                  <div className="pt-2">
                    {filtered.length === 0 && (
                      <div className="text-xs py-10 text-center" style={{ color: "var(--ink-3)" }}>
                        No matches
                      </div>
                    )}
                    {filtered.map((c) => {
                      const path = pathById[c.id] || c.name;
                      const parentPath = path.includes(" › ") ? path.split(" › ").slice(0, -1).join(" › ") : "";
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => pick(c)}
                          className="w-full text-left py-3 px-3 rounded-xl press hover:bg-black/5 active:bg-black/10"
                          data-testid={`tree-pick-${c.id}`}
                        >
                          <div className="text-[15px]" style={{ color: "var(--ink)" }}>
                            {c.name}
                          </div>
                          {parentPath && (
                            <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--ink-3)" }}>
                              {parentPath}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="pt-2">
                    {visibleFlat.map((row) => {
                      const { cat, depth, hasChildren } = row;
                      const isOpen = expanded.has(cat.id);
                      return (
                        <div
                          key={cat.id}
                          className="flex items-stretch rounded-xl press hover:bg-black/5"
                          style={{ paddingLeft: depth * 14 }}
                        >
                          {hasChildren ? (
                            <button
                              type="button"
                              onClick={() => toggle(cat.id)}
                              className="w-11 grid place-items-center shrink-0"
                              aria-label={isOpen ? "Collapse" : "Expand"}
                              style={{ color: "var(--ink-3)" }}
                            >
                              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          ) : (
                            <span className="w-11 shrink-0" />
                          )}
                          <button
                            type="button"
                            onClick={() => pick(cat)}
                            className={`flex-1 text-left py-3 pr-3 text-[15px] ${value === cat.id ? "font-semibold" : ""}`}
                            data-testid={`tree-pick-${cat.id}`}
                            style={{ color: "var(--ink)" }}
                          >
                            {cat.name}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
